import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { 
  Download, 
  BarChart3, 
  Smile, 
  Angry, 
  Meh, 
  Frown, 
  Laugh, 
  MapPin, 
  Users 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";

interface SurveyStatsProps {
  municipalityId: string;
}

interface StatsData {
  total: number;
  average: number;
  ratingsDistribution: Record<number, number>;
  originsDistribution: Record<string, number>;
  topPostalCodes: Array<{ postalCode: string | null; count: number }>;
}

const RATING_INFO = {
  1: { label: "Muy insatisfecho", icon: Angry, color: "bg-red-500", textColor: "text-red-500" },
  2: { label: "Insatisfecho", icon: Frown, color: "bg-orange-500", textColor: "text-orange-500" },
  3: { label: "Neutral", icon: Meh, color: "bg-yellow-500", textColor: "text-yellow-600" },
  4: { label: "Satisfecho", icon: Smile, color: "bg-emerald-500", textColor: "text-emerald-500" },
  5: { label: "Muy satisfecho", icon: Laugh, color: "bg-green-600", textColor: "text-green-600" },
};

const ORIGIN_LABELS: Record<string, string> = {
  ES: "🇪🇸 España",
  EU: "🇪🇺 Europa",
  AM: "🌎 América",
  OU: "🌐 Resto de países",
};

export const SurveyStats = ({ municipalityId }: SurveyStatsProps) => {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadStats();
  }, [municipalityId]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const data = await api.surveys.getStats(municipalityId);
      setStats(data);
    } catch (err) {
      console.error("Error loading survey stats:", err);
      toast({
        title: "Error",
        description: "No se pudieron cargar las estadísticas de la encuesta.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const data = await api.surveys.list(municipalityId);
      
      if (!data || data.length === 0) {
        toast({
          title: "Aviso",
          description: "No hay respuestas registradas para exportar.",
        });
        return;
      }

      // Build CSV content
      const headers = ["ID", "Fecha", "Valoracion", "Procedencia", "Codigo Postal"];
      const rows = data.map((item: any) => [
        item.id,
        new Date(item.createdAt || item.created_at).toLocaleString("es-ES"),
        item.rating,
        item.originType || item.origin_type,
        item.postalCode || item.postal_code || "",
      ]);

      const csvContent = [
        headers.join(","),
        ...rows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))
      ].join("\n");

      // Download CSV file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `encuestas_satisfaccion_${municipalityId}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: "Exportación completada",
        description: "El archivo CSV se ha descargado correctamente.",
      });
    } catch (err) {
      console.error("Error exporting survey data:", err);
      toast({
        title: "Error",
        description: "No se pudo exportar el archivo CSV.",
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-3"></div>
        Cargando estadísticas de la encuesta...
      </div>
    );
  }

  if (!stats || stats.total === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <BarChart3 className="h-5 w-5 text-primary" />
            Estadísticas de la Encuesta
          </CardTitle>
          <CardDescription>
            Visualiza las respuestas de la encuesta de satisfacción del kiosco
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center py-8 text-muted-foreground">
          No hay opiniones registradas todavía para este municipio.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2 text-lg">
            <BarChart3 className="h-5 w-5 text-primary" />
            Estadísticas de la Encuesta de Satisfacción
          </CardTitle>
          <CardDescription>
            Resultados de la valoración del kiosco y procedencia de visitantes
          </CardDescription>
        </div>
        <Button 
          type="button"
          onClick={handleExportCSV} 
          disabled={exporting}
          variant="outline"
          className="sm:self-start gap-2"
        >
          <Download className="h-4 w-4" />
          {exporting ? "Exportando..." : "Exportar a CSV"}
        </Button>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Top Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-muted/40 rounded-xl flex items-center justify-between border">
            <div>
              <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Total Opiniones</p>
              <h3 className="text-3xl font-extrabold text-foreground mt-1">{stats.total}</h3>
            </div>
            <Users className="h-8 w-8 text-primary/60" />
          </div>

          <div className="p-4 bg-muted/40 rounded-xl flex items-center justify-between border">
            <div>
              <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Valoración Media</p>
              <div className="flex items-baseline gap-2 mt-1">
                <h3 className="text-3xl font-extrabold text-foreground">{stats.average}</h3>
                <span className="text-muted-foreground text-sm">/ 5</span>
              </div>
            </div>
            <div className="flex items-center">
              {[1, 2, 3, 4, 5].map((star) => {
                const filled = star <= Math.round(stats.average);
                return (
                  <span 
                    key={star} 
                    className={`text-2xl ${filled ? "text-yellow-400" : "text-gray-300"}`}
                  >
                    ★
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Satisfaction Breakdown */}
          <div className="space-y-4">
            <h4 className="font-semibold text-sm text-foreground">Distribución de Satisfacción</h4>
            <div className="space-y-3">
              {([5, 4, 3, 2, 1] as const).map((ratingVal) => {
                const info = RATING_INFO[ratingVal];
                const count = stats.ratingsDistribution[ratingVal] || 0;
                const percentage = stats.total > 0 ? Math.round((count / stats.total) * 100) : 0;
                const Icon = info.icon;
                
                return (
                  <div key={ratingVal} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-5 h-5 ${info.textColor}`} />
                        <span className="font-medium">{info.label}</span>
                      </div>
                      <span className="text-muted-foreground font-bold">
                        {count} ({percentage}%)
                      </span>
                    </div>
                    <div className="h-3 w-full bg-muted rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${info.color} rounded-full transition-all duration-500`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Origin Breakdown */}
          <div className="space-y-4">
            <h4 className="font-semibold text-sm text-foreground">Procedencia de Visitantes</h4>
            <div className="space-y-3">
              {["ES", "EU", "AM", "OU"].map((originKey) => {
                const count = stats.originsDistribution[originKey] || 0;
                const percentage = stats.total > 0 ? Math.round((count / stats.total) * 100) : 0;
                
                return (
                  <div key={originKey} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{ORIGIN_LABELS[originKey]}</span>
                      <span className="text-muted-foreground font-bold">
                        {count} ({percentage}%)
                      </span>
                    </div>
                    <div className="h-3 w-full bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Top Postal Codes for ES */}
        {stats.topPostalCodes && stats.topPostalCodes.length > 0 && (
          <div className="space-y-3 pt-2">
            <h4 className="font-semibold text-sm text-foreground flex items-center gap-1.5">
              <MapPin className="h-4 w-4 text-primary" />
              Principales Códigos Postales (España)
            </h4>
            <div className="border rounded-xl overflow-hidden bg-muted/20">
              <table className="w-full text-sm text-left">
                <thead className="text-xs uppercase bg-muted text-muted-foreground border-b font-bold">
                  <tr>
                    <th className="px-4 py-2">Código Postal</th>
                    <th className="px-4 py-2 text-right">Total Opiniones</th>
                  </tr>
                </thead>
                <tbody className="divide-y bg-white">
                  {stats.topPostalCodes.map((row, idx) => (
                    <tr key={row.postalCode || idx} className="hover:bg-muted/10">
                      <td className="px-4 py-2.5 font-semibold text-foreground">{row.postalCode}</td>
                      <td className="px-4 py-2.5 text-right font-bold text-muted-foreground">{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
